import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import useSeo from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { categoryBadgeClass } from '@/utils/format';

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const category = categories.find((item) => item.slug === slug);
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');

  const list = useMemo(() => {
    const result = articles.filter((item) => item.category === slug);
    result.sort((a, b) => (sort === 'hot' ? b.views - a.views : a.date < b.date ? 1 : -1));
    return result;
  }, [slug, sort]);

  useSeo({
    title: category
      ? `${category.name} - 美日文化冲突观察 | 美日吃瓜`
      : '栏目不存在 | 美日吃瓜',
    description: category
      ? `${category.name}栏目：${category.desc} 收录美日吃瓜频道内该主题的相关观察文章。`
      : '该栏目暂时不存在，返回美日吃瓜首页继续围观。',
    keywords: category ? `美日吃瓜,${category.name},美日文化冲突,跨文化观察` : '美日吃瓜',
    path: `/category/${slug}`,
    jsonLd: category
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: category.name,
            url: `https://dxvfa.cn/category/${category.slug}`,
            inLanguage: 'zh-CN',
            description: category.desc,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dxvfa.cn/' },
              { '@type': 'ListItem', position: 2, name: '话题总览', item: 'https://dxvfa.cn/topics' },
              {
                '@type': 'ListItem',
                position: 3,
                name: category.name,
                item: `https://dxvfa.cn/category/${category.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-400"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">这个栏目还没开门</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            你访问的栏目可能已被合并或还在筹备中，先回到首页看看其他热闹吧。
          </p>
          <Link
            to="/"
            className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-primary-900 text-background-50">
          <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <div className="text-background-300">
              <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '话题总览', to: '/topics' }, { label: category.name }]} />
            </div>
            <div className="mt-6 flex flex-col gap-5 sm:flex-row sm:items-center">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-accent-500 text-primary-950">
                <i className={`${category.icon} text-2xl`}></i>
              </span>
              <div>
                <h1 className="font-heading text-3xl font-bold md:text-4xl">{category.name}</h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
                  {category.desc}
                </p>
              </div>
            </div>
            <p className="mt-6 text-sm text-background-300">本栏目共收录 {list.length} 篇观察</p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">栏目文章</h2>
            <div className="flex items-center gap-2 self-start rounded-full border border-background-300 bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setSort('latest')}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  sort === 'latest' ? 'bg-background-50 text-primary-600' : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                最新
              </button>
              <button
                type="button"
                onClick={() => setSort('hot')}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  sort === 'hot' ? 'bg-background-50 text-primary-600' : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                热门
              </button>
            </div>
          </div>

          {list.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-100 py-20 text-center">
              <i className="ri-draft-line text-4xl text-foreground-400"></i>
              <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">这个栏目还在整理中</p>
              <p className="mt-2 text-sm text-foreground-500">我们正在补齐素材，稍后再来看看。</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((item) => (
                <ArticleCard key={item.slug} article={item} />
              ))}
            </div>
          )}

          <div className="mt-16 border-t border-background-200 pt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">其他栏目</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {categories
                .filter((item) => item.slug !== category.slug)
                .map((item) => (
                  <Link
                    key={item.slug}
                    to={`/category/${item.slug}`}
                    className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-400 hover:bg-primary-50"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <span className="font-heading text-sm font-semibold text-foreground-950">{item.name}</span>
                  </Link>
                ))}
            </div>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-6">
            <span className="text-sm font-medium text-foreground-700">相关标签：</span>
            <span className={`rounded-full border px-3 py-1 text-xs font-medium ${categoryBadgeClass(category.slug)}`}>
              {category.name}
            </span>
            <span className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-600">
              跨文化观察
            </span>
            <span className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-600">
              美日对比
            </span>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}